import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, Phone, GraduationCap, ArrowLeft } from 'lucide-react';
// import GoogleAuth from '../../components/GoogleAuth'
import { motion } from 'framer-motion';

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('student');
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const result = await registerUser(data);
      if (result.success) {
        navigate(data.role === 'admin' ? '/admin' : '/student');
      }
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 font-sans selection:bg-lime-400/30 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-lime-500/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none" />

      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-md w-full z-10 bg-neutral-900/40 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl p-8 space-y-8"
      >
        {/* Back Button */}
        <div className="flex items-center">
          <Link
            to="/login"
            className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </Link>
        </div>

        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-2xl bg-neutral-800 border border-neutral-700 shadow-sm">
            <GraduationCap className="h-8 w-8 text-lime-400" />
          </div>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white">
            Create your account
          </h2>
          <p className="mt-2 text-sm text-neutral-400">
            Or{' '}
            <Link
              to="/login"
              className="font-medium text-lime-400 hover:text-lime-300 transition-colors"
            >
              sign in to your existing account
            </Link>
          </p>
        </div>

        {/* Form */}
        <form className="mt-6 space-y-5" onSubmit={handleSubmit(onSubmit)}>
          {/* Full Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-neutral-300 mb-1">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-5 w-5 text-neutral-500" />
              <input
                id="name"
                type="text"
                className={`w-full bg-neutral-950/50 border ${errors.name ? 'border-red-500' : 'border-neutral-800'} text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400 rounded-lg py-2.5 pl-10 pr-4 transition-all`}
                placeholder="Enter your full name"
                {...register('name', { required: 'Full name is required' })}
              />
            </div>
            {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-neutral-300 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-neutral-500" />
              <input
                id="email"
                type="email"
                className={`w-full bg-neutral-950/50 border ${errors.email ? 'border-red-500' : 'border-neutral-800'} text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400 rounded-lg py-2.5 pl-10 pr-4 transition-all`}
                placeholder="Enter your email"
                {...register('email', { required: 'Email is required' })}
              />
            </div>
            {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-neutral-300 mb-1">Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-5 w-5 text-neutral-500" />
              <input
                id="phone"
                type="tel"
                className={`w-full bg-neutral-950/50 border ${errors.phone ? 'border-red-500' : 'border-neutral-800'} text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400 rounded-lg py-2.5 pl-10 pr-4 transition-all`}
                placeholder="Enter your phone number"
                {...register('phone', { required: 'Phone number is required' })}
              />
            </div>
            {errors.phone && <p className="mt-1 text-sm text-red-400">{errors.phone.message}</p>}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-neutral-300 mb-1">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-neutral-500" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`w-full bg-neutral-950/50 border ${errors.password ? 'border-red-500' : 'border-neutral-800'} text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400 rounded-lg py-2.5 pl-10 pr-10 transition-all`}
                placeholder="Create a password"
                {...register('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                className="absolute right-3 top-3 text-neutral-500 hover:text-neutral-300"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-300 mb-1">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-neutral-500" />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                className={`w-full bg-neutral-950/50 border ${errors.confirmPassword ? 'border-red-500' : 'border-neutral-800'} text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400 rounded-lg py-2.5 pl-10 pr-10 transition-all`}
                placeholder="Confirm your password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) =>
                    value === password || 'Passwords do not match',
                })}
              />
              <button
                type="button"
                className="absolute right-3 top-3 text-neutral-500 hover:text-neutral-300"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && <p className="mt-1 text-sm text-red-400">{errors.confirmPassword.message}</p>}
          </div>

          {/* Role */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-neutral-300 mb-1">Register as</label>
            <select
              id="role"
              className={`w-full bg-neutral-950/50 border ${errors.role ? 'border-red-500' : 'border-neutral-800'} text-white focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400 rounded-lg py-2.5 px-4 transition-all appearance-none`}
              {...register('role', { required: 'Please select a role' })}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="" className="bg-neutral-900">Select role</option>
              <option value="student" className="bg-neutral-900">Student</option>
              {/* <option value="admin" className="bg-neutral-900">Admin</option> */}
            </select>
            {errors.role && <p className="mt-1 text-sm text-red-400">{errors.role.message}</p>}
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-neutral-950 font-semibold bg-lime-400 hover:bg-lime-300 transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-lime-500 focus:ring-offset-neutral-950 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-neutral-950 mr-2"></div>
                  Creating account...
                </div>
              ) : (
                'Create account'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default Register;
